import React, { useState, useEffect } from 'react';
import { 
  Modal, FormControl, Box, Tooltip,Alert,Snackbar, Typography, TextField, 
  Button, Checkbox, FormControlLabel, FormGroup, IconButton 
} from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import { grey, red,blue } from '@mui/material/colors';
import BASE_URL from '../../config'

const style = {
  position: 'absolute', 
  top: '50%', 
  left: '50%', 
  transform: 'translate(-50%, -50%)',
  bgcolor: 'white', 
  borderRadius: 4, 
  p: 4, 
  outline: 'none',
  maxHeight: 'auto', 
  width: { xs: '90%', sm: '500px' }, 
  display: 'flex',
  flexDirection: 'column'
};

const Inputstyle = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#f5f5f5',
    borderRadius: '8px',
    '& fieldset': { borderColor: 'transparent' },
    '&:hover fieldset': { borderColor: grey[400] },
    '&.Mui-focused fieldset': { borderColor: grey[600] },
    '&.Mui-error fieldset': { borderColor: red[700] },
  },
};

const listatipo = [
  'Eléctrico', 'Mecánico','Soporte','ingenieria'
];

export default function Editsystem({ equipo, refresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: '', descripcion: '', tipo: [] });
  const [confirmType, setConfirmType] = useState(null); // 'save' o 'cancel'
  const [error, setError] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isNameFocused, setIsNameFocused] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (equipo && open) {
      setForm({
        nombre: equipo.nombre || '',
        descripcion: equipo.descripcion || '',
        tipo: Array.isArray(equipo.tipo) ? equipo.tipo : []
      });
      setError(false);
      setConfirmType(null);
    }
  }, [equipo, open]);

  const handleCheck = (p) => {
    const nuevos = form.tipo.includes(p)
      ? form.tipo.filter(item => item !== p)
      : [...form.tipo, p];
    setForm({ ...form, tipo: nuevos });
  };

  const validateAndOpenSave = () => {
    const isNombreEmpty = !form.nombre.trim();
    const isDescEmpty = !form.descripcion.trim();
    const istipoEmpty = form.tipo.length === 0;

    if (isNombreEmpty || isDescEmpty || istipoEmpty) {
      setError(true);
    } else {
      setError(false);
      setConfirmType('save');
    }
  };

  const handleUpdate = async () => {
    try {
      const res = await fetch(`${BASE_URL}/equipos/${equipo.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSnackbar({ open: true, message: 'Se realizaron los ajustes con éxito', severity: 'success' });
        setOpen(false);
        refresh();
      } else {
        setSnackbar({ open: true, message: data.message || 'No se pudo actualizar el equipo', severity: 'error' });
      }
    } catch (e) { setSnackbar({ open: true, message: 'Error de conexión con el servidor', severity: 'error' }); }
  };

  const ConfirmView = () => (
    <Box sx={style}>
      <Typography variant="h6">
        {confirmType === 'cancel' ? '¿Cancelar edición?' : '¿Guardar cambios?'}
      </Typography>
      <Typography variant="body1" color='textSecondary' sx={{ mt: 1, mb: 3 }}>
        {confirmType === 'cancel' 
          ? '¿Estás seguro que deseas cancelar? Los cambios realizados se perderán.' 
          : '¿Estás seguro que deseas aplicar los cambios a este equipo?'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'end' }}>
        <Button 
          variant="contained" 
          sx={{ bgcolor: grey[200], color: 'black', textTransform: 'none', px: 3}} 
          onClick={() => setConfirmType(null)}
        >
          {confirmType === 'cancel' ? 'No, continuar' : 'No, revisar'}
        </Button>
        <Button 
          variant="contained" 
          sx={{ bgcolor: 'black', color: 'white', textTransform: 'none', px: 3}}
          onClick={confirmType === 'cancel' ? () => setOpen(false) : handleUpdate}
        >
          {confirmType === 'cancel' ? 'Sí, cancelar' : 'Sí, guardar'}
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <IconButton onClick={() => setOpen(true)} 
      sx={{'&:hover': {color:grey[100], bgcolor: blue[400] } }}>
        <EditIcon fontSize="small" />
      </IconButton>

      <Modal open={open} onClose={() => setConfirmType('cancel')}>
        <Box>
          {confirmType ? <ConfirmView /> : (
            <Box sx={style}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6">Editar equipo/solución</Typography>
                <Typography variant="body2" color='textSecondary'>Actualiza la información y tipo del equipo</Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* NOMBRE */}
                <FormControl>
                  <Typography variant="body2" fontWeight="500" sx={{ mb: 0.5 }}>Nombre del equipo</Typography>
                  <Tooltip 
                    title="Debes completar este campo" 
                    open={(isNameFocused || error) && !form.nombre.trim()} 
                    placement="top-end" arrow
                    componentsProps={{ tooltip: { sx: { bgcolor: red[700] } }, arrow: { sx: { color: red[700] } } }}
                  >
                    <TextField 
                      fullWidth variant="outlined" sx={Inputstyle} 
                      error={error && !form.nombre.trim()}
                      value={form.nombre}
                      onFocus={() => setIsNameFocused(true)}
                      onBlur={() => setIsNameFocused(false)}
                      onChange={(e) => setForm({...form, nombre: e.target.value})} 
                    />
                  </Tooltip>
                </FormControl>

                {/* DESCRIPCIÓN */}
                <FormControl>
                  <Typography variant="body2" fontWeight="500" sx={{ mb: 0.5 }}>Descripción</Typography>
                  <Tooltip 
                    title="Debes completar este campo" 
                    open={(isFocused || error) && !form.descripcion.trim()} 
                    placement="top-end" arrow
                    componentsProps={{ tooltip: { sx: { bgcolor: red[700] } }, arrow: { sx: { color: red[700] } } }}
                  >
                    <TextField 
                      fullWidth multiline rows={2} variant="outlined" sx={Inputstyle}
                      error={error && !form.descripcion.trim()}
                      value={form.descripcion}
                      onFocus={() => setIsFocused(true)}
                      onBlur={() => setIsFocused(false)}
                      onChange={(e) => setForm({...form, descripcion: e.target.value})} 
                    />
                  </Tooltip>
                </FormControl>

                {/* TIPO */}
                <FormGroup sx={{mb:1}}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>Tipo de solución</Typography>
                  <Tooltip 
                    title="Debes seleccionar al menos un tipo" 
                    open={error && form.tipo.length === 0} 
                    placement="top-end" arrow
                    componentsProps={{ tooltip: { sx: { bgcolor: red[700] } }, arrow: { sx: { color: red[700] } } }}
                  >
                    <Box sx={{ 
                      border: `1px solid ${error && form.tipo.length === 0 ? red[700] : grey[300]}`, 
                      borderRadius: '8px', p: 1 
                    }}>
                      {listatipo.map((p) => (
                        <Box key={p} sx={{ display: 'flex', alignItems: 'center', height: '32px' }}>
                          <FormControlLabel
                            control={<Checkbox size="small" checked={form.tipo.includes(p)} onChange={() => handleCheck(p)} />}
                            label={<Typography variant="body2">{p}</Typography>}
                            sx={{ margin: 0, width: '100%' }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Tooltip>
                </FormGroup>

                {/* BOTONES ACCIÓN */}
                <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                  <Button 
                    variant="contained" 
                    sx={{ bgcolor: grey[100], color: 'black', textTransform: 'none',width: { xs: '40%', sm: '25%' },mr:1, '&:hover': { bgcolor: grey[200] } }}
                    onClick={() => setConfirmType('cancel')}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    variant="contained" 
                    sx={{ bgcolor: 'black', color: 'white', textTransform: 'none',width: { xs: '55%', sm: '35%' },ml:1, '&:hover': { bgcolor: '#333' } }}
                    onClick={validateAndOpenSave}
                  >
                    Guardar Cambios
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>
      <Snackbar open={snackbar.open} autoHideDuration={2500} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: '10px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}